<script setup>
import { computed, ref, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsHeader from 'dashboard/components-next/aifocus/pageComponents/settings/SettingsHeader.vue';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

const route = useRoute();
const store = useStore();
const accountId = computed(() => route.params.accountId);

const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](props.agentId)
);

const isSaving = ref(false);
const editingSchemaIndex = ref(null);
const editingProductIndex = ref(null);
const isUploadingImage = ref(false);

// Dialog refs
const schemaDialogRef = ref(null);
const productDialogRef = ref(null);

// Lead config state (saved in agent config)
const ragEnabled = ref(false);
const ragTopK = ref(5);
const ragThreshold = ref(0.3);
const productKeywords = ref('');
const intentDetectionMode = ref('keywords');
const schemas = ref([]);
const products = ref([]);

const fieldTypeOptions = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Numero' },
  { value: 'currency', label: 'Valor (R$)' },
  { value: 'select', label: 'Selecao' },
  { value: 'boolean', label: 'Sim/Nao' },
];

const intentModeOptions = [
  { value: 'keywords', label: 'Por palavras-chave' },
  { value: 'auto', label: 'Deteccao automatica (IA)' },
  { value: 'always', label: 'Sempre ativo' },
];

// Schema form
const schemaForm = reactive({
  name: '',
  description: '',
  fields: [],
});

// Product form
const productForm = reactive({
  name: '',
  schema_name: '',
  image_url: '',
  description: '',
  fields: {},
});

const schemaOptions = computed(() =>
  schemas.value.map(s => ({ value: s.name, label: s.name }))
);

const loadFromAgent = agentData => {
  if (!agentData) return;
  const config = agentData.config || {};
  ragEnabled.value = config.rag_enabled ?? false;
  ragTopK.value = config.rag_top_k || 5;
  ragThreshold.value = config.rag_threshold || 0.3;
  productKeywords.value = (config.product_keywords || []).join(', ');
  intentDetectionMode.value = config.intent_detection_mode || 'keywords';
  schemas.value = config.lead_schemas || [];
  products.value = config.rag_products || [];
};

watch(
  agent,
  newAgent => {
    if (newAgent) loadFromAgent(newAgent);
  },
  { immediate: true, deep: false }
);

const handleSave = async () => {
  isSaving.value = true;
  try {
    const keywords = productKeywords.value
      ? productKeywords.value
          .split(',')
          .map(k => k.trim())
          .filter(Boolean)
      : [];

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      config: {
        ...(agent.value?.config || {}),
        rag_enabled: ragEnabled.value,
        rag_top_k: Number(ragTopK.value),
        rag_threshold: Number(ragThreshold.value),
        product_keywords: keywords,
        intent_detection_mode: intentDetectionMode.value,
        lead_schemas: schemas.value,
        rag_products: products.value,
      },
    });
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    await store.dispatch('aifocusAgents/show', props.agentId);
    useAlert('Produtos salvo e sincronizado');
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar');
  } finally {
    isSaving.value = false;
  }
};

// Schema CRUD
const resetSchemaForm = () => {
  schemaForm.name = '';
  schemaForm.description = '';
  schemaForm.fields = [];
  editingSchemaIndex.value = null;
};

const openNewSchema = () => {
  resetSchemaForm();
  schemaDialogRef.value.open();
};

const openEditSchema = (schema, index) => {
  editingSchemaIndex.value = index;
  schemaForm.name = schema.name || '';
  schemaForm.description = schema.description || '';
  schemaForm.fields = (schema.fields || []).map(f => ({
    ...f,
    options: Array.isArray(f.options) ? f.options.join(', ') : f.options || '',
  }));
  schemaDialogRef.value.open();
};

const addField = () => {
  schemaForm.fields.push({
    key: '',
    label: '',
    type: 'text',
    required: false,
    options: '',
    show_in_rag: true,
    rag_label: '',
  });
};

const removeField = index => {
  schemaForm.fields.splice(index, 1);
};

const saveSchema = () => {
  if (!schemaForm.name) {
    useAlert('Nome do esquema e obrigatorio');
    return;
  }
  if (schemaForm.fields.length === 0) {
    useAlert('Adicione pelo menos um campo');
    return;
  }

  const fields = schemaForm.fields.map(f => ({
    ...f,
    key:
      f.key ||
      f.label
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, ''),
    options:
      typeof f.options === 'string'
        ? f.options
            .split(',')
            .map(o => o.trim())
            .filter(Boolean)
        : f.options || [],
  }));

  const schemaData = {
    name: schemaForm.name,
    description: schemaForm.description,
    fields,
  };

  const updated = [...schemas.value];
  if (editingSchemaIndex.value !== null) {
    updated[editingSchemaIndex.value] = schemaData;
  } else {
    updated.push(schemaData);
  }
  schemas.value = updated;
  schemaDialogRef.value.close();
  useAlert('Esquema salvo. Clique "Salvar Configuracao" para confirmar.');
};

const deleteSchema = index => {
  schemas.value = schemas.value.filter((_, i) => i !== index);
  useAlert('Esquema removido. Clique "Salvar Configuracao" para confirmar.');
};

// Product CRUD
const selectedSchemaFields = computed(() => {
  if (!productForm.schema_name) return [];
  const schema = schemas.value.find(s => s.name === productForm.schema_name);
  return schema?.fields || [];
});

const resetProductForm = () => {
  productForm.name = '';
  productForm.schema_name =
    schemas.value.length > 0 ? schemas.value[0].name : '';
  productForm.image_url = '';
  productForm.description = '';
  productForm.fields = {};
  editingProductIndex.value = null;
};

const openNewProduct = () => {
  resetProductForm();
  productDialogRef.value.open();
};

const openEditProduct = (product, index) => {
  editingProductIndex.value = index;
  productForm.name = product.name || '';
  productForm.schema_name = product.schema_name || '';
  productForm.image_url = product.image_url || '';
  productForm.description = product.description || '';
  productForm.fields = { ...(product.fields || {}) };
  productDialogRef.value.open();
};

const handleImageUpload = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    useAlert('Selecione apenas arquivos de imagem');
    return;
  }

  if (file.size > 10 * 1024 * 1024) {
    useAlert('Imagem deve ter no maximo 10MB');
    return;
  }

  isUploadingImage.value = true;
  try {
    const result = await uploadFile(file, accountId.value);
    productForm.image_url = result.fileUrl;
    useAlert('Imagem enviada com sucesso');
  } catch (error) {
    useAlert(error?.message || 'Erro ao enviar imagem');
  } finally {
    isUploadingImage.value = false;
  }
};

const removeProductImage = () => {
  productForm.image_url = '';
};

const saveProduct = () => {
  if (!productForm.name) {
    useAlert('Nome do produto e obrigatorio');
    return;
  }

  const productData = {
    name: productForm.name,
    schema_name: productForm.schema_name,
    image_url: productForm.image_url,
    description: productForm.description,
    fields: { ...productForm.fields },
  };

  const updated = [...products.value];
  if (editingProductIndex.value !== null) {
    updated[editingProductIndex.value] = productData;
  } else {
    updated.push(productData);
  }
  products.value = updated;
  productDialogRef.value.close();
  useAlert('Produto salvo. Clique "Salvar Configuracao" para confirmar.');
};

const deleteProduct = index => {
  products.value = products.value.filter((_, i) => i !== index);
  useAlert('Produto removido. Clique "Salvar Configuracao" para confirmar.');
};

const getProductSchemaFields = product => {
  const schema = schemas.value.find(s => s.name === product.schema_name);
  return schema?.fields || [];
};

const getFieldSelectOptions = field => {
  if (!Array.isArray(field.options)) return [];
  return [
    { value: '', label: 'Selecione...' },
    ...field.options.map(opt => ({ value: opt, label: opt })),
  ];
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="pb-6">
    <SettingsHeader
      heading="Leads & Produtos"
      description="Configure esquemas, cadastre produtos com imagens e busca inteligente."
    />

    <!-- Action Buttons -->
    <div class="flex items-center justify-end gap-2 mt-4 mb-6">
      <Button
        label="Novo Esquema"
        icon="i-lucide-plus"
        variant="secondary"
        @click="openNewSchema"
      />
      <Button
        label="Novo Produto"
        icon="i-lucide-package-plus"
        :disabled="schemas.length === 0"
        @click="openNewProduct"
      />
    </div>

    <!-- Products Section -->
    <div class="mb-8">
      <h4
        class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
      >
        <span class="i-lucide-package text-n-slate-11" />
        Produtos Cadastrados
        <span
          v-if="products.length > 0"
          class="px-2 py-0.5 text-xs bg-n-alpha-2 text-n-slate-10 rounded-full"
        >
          {{ products.length }}
        </span>
      </h4>

      <div
        v-if="products.length === 0"
        class="flex flex-col items-center justify-center py-12 text-center bg-n-alpha-1 border border-dashed border-n-weak rounded-xl"
      >
        <div
          class="w-12 h-12 mb-3 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-package text-2xl text-n-slate-10" />
        </div>
        <h4 class="text-md font-medium text-n-slate-12 mb-1">
          Nenhum produto cadastrado
        </h4>
        <p class="text-sm text-n-slate-11 mb-4 max-w-md">
          Cadastre produtos com imagens para o agente apresentar ao cliente.
          {{ schemas.length === 0 ? 'Crie um esquema primeiro.' : '' }}
        </p>
        <Button
          v-if="schemas.length > 0"
          label="Cadastrar Produto"
          icon="i-lucide-plus"
          variant="secondary"
          @click="openNewProduct"
        />
        <Button
          v-else
          label="Criar Esquema Primeiro"
          icon="i-lucide-plus"
          variant="secondary"
          @click="openNewSchema"
        />
      </div>

      <div v-else class="grid gap-3 sm:grid-cols-2">
        <div
          v-for="(product, index) in products"
          :key="index"
          class="flex gap-3 p-4 bg-n-solid-1 border border-n-weak rounded-xl hover:border-n-slate-6 transition-colors"
        >
          <!-- Product Image -->
          <div
            class="w-20 h-20 rounded-lg bg-n-alpha-2 flex-shrink-0 overflow-hidden flex items-center justify-center"
          >
            <img
              v-if="product.image_url"
              :src="product.image_url"
              :alt="product.name"
              class="w-full h-full object-cover"
            />
            <span v-else class="i-lucide-image text-2xl text-n-slate-10" />
          </div>

          <!-- Product Info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="font-medium text-n-slate-12 truncate">
                  {{ product.name }}
                </p>
                <p v-if="product.schema_name" class="text-xs text-n-slate-10">
                  {{ product.schema_name }}
                </p>
              </div>
              <div class="flex items-center gap-1 flex-shrink-0">
                <Button
                  icon="i-lucide-pencil"
                  variant="ghost"
                  size="sm"
                  @click="openEditProduct(product, index)"
                />
                <ConfirmButton
                  icon="i-lucide-trash-2"
                  variant="ghost"
                  size="sm"
                  color="ruby"
                  label=""
                  confirm-label=""
                  confirm-hint="Confirmar?"
                  @click="deleteProduct(index)"
                />
              </div>
            </div>
            <p
              v-if="product.description"
              class="text-xs text-n-slate-11 mt-1 line-clamp-2"
            >
              {{ product.description }}
            </p>
            <!-- Field values preview -->
            <div
              v-if="product.fields && Object.keys(product.fields).length > 0"
              class="flex flex-wrap gap-1 mt-2"
            >
              <span
                v-for="(val, key) in product.fields"
                :key="key"
                class="px-1.5 py-0.5 text-xs bg-n-alpha-2 text-n-slate-10 rounded"
              >
                {{
                  getProductSchemaFields(product).find(f => f.key === key)
                    ?.label || key
                }}:
                {{ val }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Schemas Section -->
    <div class="mb-8">
      <h4
        class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
      >
        <span class="i-lucide-database text-n-slate-11" />
        Esquemas de Dados
      </h4>

      <div
        v-if="schemas.length === 0"
        class="flex flex-col items-center justify-center py-12 text-center bg-n-alpha-1 border border-dashed border-n-weak rounded-xl"
      >
        <div
          class="w-12 h-12 mb-3 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-database text-2xl text-n-slate-10" />
        </div>
        <h4 class="text-md font-medium text-n-slate-12 mb-1">
          Nenhum esquema criado
        </h4>
        <p class="text-sm text-n-slate-11 mb-4 max-w-md">
          Crie esquemas para definir os campos dos seus produtos (ex: nome, ano,
          km, preco).
        </p>
        <Button
          label="Criar Primeiro Esquema"
          icon="i-lucide-plus"
          variant="secondary"
          @click="openNewSchema"
        />
      </div>

      <div v-else class="grid gap-3">
        <div
          v-for="(schema, index) in schemas"
          :key="index"
          class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl hover:border-n-slate-6 transition-colors"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center"
            >
              <span class="i-lucide-database text-xl text-violet-400" />
            </div>
            <div>
              <p class="font-medium text-n-slate-12">{{ schema.name }}</p>
              <p class="text-xs text-n-slate-10">
                {{ (schema.fields || []).length }} campos
                <span v-if="schema.description" class="mx-1">&middot;</span>
                {{ schema.description }}
              </p>
              <div
                v-if="schema.fields && schema.fields.length > 0"
                class="flex flex-wrap gap-1 mt-1"
              >
                <span
                  v-for="field in schema.fields.slice(0, 5)"
                  :key="field.key || field.label"
                  class="px-1.5 py-0.5 text-xs bg-n-alpha-2 text-n-slate-10 rounded"
                >
                  {{ field.label || field.key }}
                </span>
                <span
                  v-if="schema.fields.length > 5"
                  class="px-1.5 py-0.5 text-xs bg-n-alpha-2 text-n-slate-10 rounded"
                >
                  +{{ schema.fields.length - 5 }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              icon="i-lucide-pencil"
              variant="ghost"
              size="sm"
              @click="openEditSchema(schema, index)"
            />
            <ConfirmButton
              icon="i-lucide-trash-2"
              variant="ghost"
              size="sm"
              color="ruby"
              label=""
              confirm-label=""
              confirm-hint="Confirmar?"
              @click="deleteSchema(index)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- RAG Config -->
    <div class="mb-8">
      <h4
        class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
      >
        <span class="i-lucide-sparkles text-n-slate-11" />
        Busca Inteligente de Produtos
      </h4>

      <div class="space-y-5 p-5 bg-n-alpha-1 border border-n-weak rounded-xl">
        <!-- Enable toggle -->
        <div class="flex items-center justify-between">
          <div>
            <p class="font-medium text-n-slate-12">
              Ativar busca de produtos/leads
            </p>
            <p class="text-xs text-n-slate-10">
              O agente busca informacoes de produtos quando detecta interesse
            </p>
          </div>
          <Switch v-model="ragEnabled" />
        </div>

        <template v-if="ragEnabled">
          <!-- Intent detection mode -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              Modo de deteccao de interesse
            </label>
            <ComboBox
              v-model="intentDetectionMode"
              :options="intentModeOptions"
              placeholder="Selecione..."
            />
          </div>

          <!-- Keywords (only for keywords mode) -->
          <div v-if="intentDetectionMode === 'keywords'">
            <Input
              v-model="productKeywords"
              label="Palavras-chave de produto"
              placeholder="preco, quanto custa, disponivel, catalogo"
              message="Separadas por virgula. Quando detectadas, o agente busca produtos."
            />
          </div>

          <!-- Advanced RAG settings -->
          <div class="grid grid-cols-2 gap-4">
            <Input
              v-model="ragTopK"
              label="Resultados maximos"
              type="number"
              min="1"
              max="20"
            />
            <Input
              v-model="ragThreshold"
              label="Similaridade minima"
              type="number"
              min="0"
              max="1"
              step="0.1"
            />
          </div>
        </template>
      </div>
    </div>

    <!-- Save Button -->
    <div class="flex justify-end mb-6">
      <Button
        label="Salvar Configuracao"
        :is-loading="isSaving"
        @click="handleSave"
      />
    </div>

    <!-- Info Card -->
    <div class="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
      <div class="flex gap-3">
        <span class="i-lucide-info text-blue-400 text-xl flex-shrink-0" />
        <div>
          <h4 class="font-medium text-n-slate-12 mb-1">
            Sobre Leads & Produtos
          </h4>
          <p class="text-sm text-n-slate-11">
            Esquemas definem os campos dos seus produtos (ex: veiculos com ano,
            km, preco). Cadastre produtos com imagens para o agente apresentar
            ao cliente de forma inteligente.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Schema Dialog -->
  <Dialog
    ref="schemaDialogRef"
    :title="
      (editingSchemaIndex !== null ? 'Editar' : 'Novo') + ' Esquema de Dados'
    "
    description="Defina os campos que representam seu produto ou lead."
    confirm-button-label="Salvar Esquema"
    :is-loading="isSaving"
    overflow-y-auto
    width="lg"
    @confirm="saveSchema"
  >
    <div class="space-y-4">
      <Input
        v-model="schemaForm.name"
        label="Nome do Esquema"
        placeholder="Ex: Veiculos, Imoveis, Servicos"
      />

      <Input
        v-model="schemaForm.description"
        label="Descricao"
        placeholder="Breve descricao do tipo de produto"
      />

      <!-- Fields -->
      <div>
        <div class="flex items-center justify-between mb-3">
          <label class="text-sm font-medium text-n-slate-12">Campos</label>
          <Button
            label="Adicionar Campo"
            icon="i-lucide-plus"
            variant="ghost"
            size="sm"
            @click="addField"
          />
        </div>

        <div
          v-if="schemaForm.fields.length === 0"
          class="text-center py-8 bg-n-alpha-1 border border-dashed border-n-weak rounded-lg"
        >
          <p class="text-sm text-n-slate-10">
            Nenhum campo. Clique em "Adicionar Campo".
          </p>
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="(field, index) in schemaForm.fields"
            :key="index"
            class="p-4 bg-n-alpha-1 border border-n-weak rounded-lg space-y-3"
          >
            <!-- Row 1: Name + Type -->
            <div class="grid grid-cols-[1fr_auto] gap-2">
              <Input
                v-model="field.label"
                placeholder="Nome do campo (ex: Ano)"
                size="sm"
              />
              <ComboBox
                v-model="field.type"
                :options="fieldTypeOptions"
                placeholder="Tipo"
              />
            </div>

            <!-- Options for select type -->
            <Input
              v-if="field.type === 'select'"
              v-model="field.options"
              placeholder="Opcoes separadas por virgula (ex: Novo, Usado, Semi-novo)"
              size="sm"
            />

            <!-- Row 2: RAG label -->
            <Input
              v-model="field.rag_label"
              placeholder="Label para busca (ex: Ano do veiculo)"
              size="sm"
            />

            <!-- Row 3: Switches + Delete -->
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-4">
                <div class="flex items-center gap-1.5">
                  <Switch v-model="field.required" />
                  <span class="text-sm text-n-slate-11">Obrigatorio</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <Switch v-model="field.show_in_rag" />
                  <span class="text-sm text-n-slate-11">Exibir na busca</span>
                </div>
              </div>
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                size="sm"
                color="ruby"
                @click="removeField(index)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>

  <!-- Product Dialog -->
  <Dialog
    ref="productDialogRef"
    :title="(editingProductIndex !== null ? 'Editar' : 'Novo') + ' Produto'"
    description="Cadastre um produto com imagem e dados do esquema."
    confirm-button-label="Salvar Produto"
    :is-loading="isSaving"
    overflow-y-auto
    width="lg"
    @confirm="saveProduct"
  >
    <div class="space-y-4">
      <!-- Image Upload -->
      <div>
        <label class="text-sm font-medium text-n-slate-12 mb-2 block">
          Imagem do Produto
        </label>
        <div class="flex items-start gap-4">
          <div
            class="w-32 h-32 rounded-xl bg-n-alpha-2 border-2 border-dashed border-n-weak overflow-hidden flex items-center justify-center relative group"
          >
            <img
              v-if="productForm.image_url"
              :src="productForm.image_url"
              alt="Preview"
              class="w-full h-full object-cover"
            />
            <div
              v-else
              class="flex flex-col items-center gap-1 text-n-slate-10"
            >
              <span class="i-lucide-image-plus text-2xl" />
              <span class="text-xs">Sem imagem</span>
            </div>
            <!-- Remove overlay -->
            <button
              v-if="productForm.image_url"
              class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              @click="removeProductImage"
            >
              <span class="i-lucide-trash-2 text-white text-xl" />
            </button>
          </div>
          <div class="flex flex-col gap-2">
            <label
              class="inline-flex items-center gap-2 px-4 py-2 bg-n-solid-1 border border-n-weak rounded-lg text-sm text-n-slate-12 cursor-pointer hover:bg-n-alpha-2 transition-colors"
            >
              <span class="i-lucide-upload text-n-slate-11" />
              {{ isUploadingImage ? 'Enviando...' : 'Enviar Imagem' }}
              <input
                type="file"
                accept="image/*"
                class="hidden"
                :disabled="isUploadingImage"
                @change="handleImageUpload"
              />
            </label>
            <p class="text-xs text-n-slate-10">JPG, PNG ou WebP. Max 10MB.</p>
          </div>
        </div>
      </div>

      <!-- Product Name -->
      <Input
        v-model="productForm.name"
        label="Nome do Produto"
        placeholder="Ex: Sandero 2019 20.000km"
      />

      <!-- Schema Select -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12"> Esquema </label>
        <ComboBox
          v-model="productForm.schema_name"
          :options="schemaOptions"
          placeholder="Selecione um esquema..."
        />
      </div>

      <!-- Description -->
      <TextArea
        v-model="productForm.description"
        label="Descricao"
        auto-height
        :min-height="60"
        placeholder="Descricao breve do produto"
      />

      <!-- Schema Fields -->
      <div v-if="selectedSchemaFields.length > 0">
        <label class="text-sm font-medium text-n-slate-12 mb-3 block">
          Dados do Produto
        </label>
        <div class="space-y-3">
          <div v-for="field in selectedSchemaFields" :key="field.key">
            <!-- Select type -->
            <template
              v-if="field.type === 'select' && Array.isArray(field.options)"
            >
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11">
                  {{ field.label || field.key }}
                  <span v-if="field.required" class="text-red-400">*</span>
                </label>
                <ComboBox
                  :model-value="productForm.fields[field.key] || ''"
                  :options="getFieldSelectOptions(field)"
                  placeholder="Selecione..."
                  @update:model-value="v => (productForm.fields[field.key] = v)"
                />
              </div>
            </template>
            <!-- Boolean type -->
            <template v-else-if="field.type === 'boolean'">
              <div class="flex items-center justify-between py-1">
                <label class="text-xs font-medium text-n-slate-11">
                  {{ field.label || field.key }}
                  <span v-if="field.required" class="text-red-400">*</span>
                </label>
                <Switch
                  :model-value="!!productForm.fields[field.key]"
                  @change="
                    productForm.fields[field.key] =
                      !productForm.fields[field.key]
                  "
                />
              </div>
            </template>
            <!-- Number / Currency -->
            <template
              v-else-if="field.type === 'number' || field.type === 'currency'"
            >
              <Input
                :model-value="productForm.fields[field.key]"
                :label="
                  (field.label || field.key) + (field.required ? ' *' : '')
                "
                type="number"
                :placeholder="field.type === 'currency' ? '0.00' : '0'"
                :step="field.type === 'currency' ? '0.01' : '1'"
                @update:model-value="v => (productForm.fields[field.key] = v)"
              />
            </template>
            <!-- Text (default) -->
            <template v-else>
              <Input
                :model-value="productForm.fields[field.key]"
                :label="
                  (field.label || field.key) + (field.required ? ' *' : '')
                "
                :placeholder="field.rag_label || field.label || field.key"
                @update:model-value="v => (productForm.fields[field.key] = v)"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
